import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, QueryStatus, useToast } from "@cx/ui";
import type { StockSettings, WarehouseInput, WarehouseView } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

const EMPTY: WarehouseInput = { name: "", isDefault: false, countsInTotal: true, active: true };

/** Depósitos (RF-SUP-02): cadastro, padrão, "considera no saldo total", situação e a configuração do negativo. */
export function WarehousesPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const key = ["warehouses", companyId];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ data: WarehouseView[] }>("/warehouses") });
  const [editing, setEditing] = useState<{ id?: string; values: WarehouseInput } | null>(null);
  const [deleting, setDeleting] = useState<WarehouseView | null>(null);
  const changed = () => {
    qc.invalidateQueries({ queryKey: key });
    qc.invalidateQueries({ queryKey: ["stock-balances", companyId] });
  };
  const del = useMutation({
    mutationFn: (id: string) => api(`/warehouses/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Depósito excluído");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });

  return (
    <>
      <ContentHeader
        title="Depósitos"
        breadcrumb={["Suprimentos", "Depósitos"]}
        actions={
          can(perms, "warehouses", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing({ values: EMPTY })}>
              Incluir depósito
            </button>
          )
        }
      />
      <div className="box card mb-3">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Depósitos">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Padrão</th>
                  <th scope="col">Considera no saldo total</th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((w) => (
                  <tr key={w.id}>
                    <td>{w.name}</td>
                    <td>{w.isDefault ? "Sim" : "Não"}</td>
                    <td>{w.countsInTotal ? "Sim" : "Não"}</td>
                    <td>{w.active ? "Ativo" : "Inativo"}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "warehouses", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${w.name}`}
                          onClick={() => setEditing({ id: w.id, values: { ...w } })}
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "warehouses", "delete") && !w.isDefault && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${w.name}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting(w)}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <StockSettingsBox />
      {editing && (
        <WarehouseDialog
          id={editing.id}
          initial={editing.values}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir depósito"
        message={`Excluir o depósito "${deleting?.name ?? ""}"? Ele pode ser restaurado pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function StockSettingsBox() {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["stock-settings", companyId];
  const settings = useQuery({ queryKey: key, queryFn: () => api<StockSettings>("/stock/settings") });
  const save = useMutation({
    mutationFn: (s: StockSettings) => api("/stock/settings", { method: "PUT", body: JSON.stringify(s) }),
    onSuccess: () => {
      toast("success", "Configuração salva");
      qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  if (!can(me.permissions as Permissions, "stock", "read")) return null;
  return (
    <div className="box card p-3">
      <h2 className="h6">Configurações do estoque</h2>
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {settings.isSuccess && (
        <div className="form-check">
          <input
            id="allow-negative"
            type="checkbox"
            className="form-check-input"
            checked={settings.data.allowNegative}
            disabled={!can(me.permissions as Permissions, "stock", "update") || save.isPending}
            onChange={(e) => save.mutate({ allowNegative: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="allow-negative">
            Permitir estoque negativo
          </label>
          <div className="form-text">
            Desligado, saídas, transferências e reservas acima do disponível são recusadas.
          </div>
        </div>
      )}
    </div>
  );
}

function WarehouseDialog({
  id,
  initial,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: WarehouseInput;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api(id ? `/warehouses/${id}` : "/warehouses", { method: id ? "PUT" : "POST", body: JSON.stringify(v) }),
    onSuccess: () => {
      toast("success", "Depósito salvo");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_warehouse") setErrors({ name: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const check = (label: string, k: "isDefault" | "countsInTotal" | "active", disabled = false) => (
    <div className="form-check mb-2">
      <input
        id={`wh-${k}`}
        type="checkbox"
        className="form-check-input"
        checked={v[k]}
        disabled={disabled}
        onChange={(e) => setV((x) => ({ ...x, [k]: e.target.checked }))}
      />
      <label className="form-check-label" htmlFor={`wh-${k}`}>
        {label}
      </label>
    </div>
  );
  return (
    <Modal
      open={open}
      title={id ? "Editar depósito" : "Novo depósito"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-12" error={errors.name}>
          <input
            value={v.name}
            maxLength={60}
            data-autofocus
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </Field>
      </div>
      {/* O padrão só troca marcando outro depósito como padrão. */}
      {check("Depósito padrão", "isDefault", initial.isDefault)}
      {check("Considera no saldo total", "countsInTotal")}
      {check("Ativo", "active")}
    </Modal>
  );
}
